import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppLayout,
  SideNavigation,
  TopNavigation,
} from '@cloudscape-design/components';
import { useAuth, usePermissions } from '@features/auth';
import { BreadcrumbNav } from '@features/configs/components/BreadcrumbNav';

/**
 * Main navigable layout with Cloudscape AppLayout.
 * Includes top navigation (branding + sign out) and side navigation.
 *
 * Requirements: 1.7, 1.8
 */
export function NavigableLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleSignOut, email } = useAuth();
  const { canWrite, canUploadCampaigns } = usePermissions();

  return (
    <>
      <TopNavigation
        identity={{
          href: '/',
          title: 'Connect Portal',
          logo: {
            src: '/tower-logo.svg',
            alt: 'Tower',
          },
        }}
        utilities={[
          {
            type: 'menu-dropdown',
            text: email ?? 'User',
            iconName: 'user-profile',
            items: [
              { id: 'signout', text: 'Sign out' },
            ],
            onItemClick: ({ detail }) => {
              if (detail.id === 'signout') {
                handleSignOut();
              }
            },
          },
        ]}
      />
      <AppLayout
        navigation={
          <SideNavigation
            activeHref={location.pathname}
            onFollow={(event) => {
              event.preventDefault();
              navigate(event.detail.href);
            }}
            items={[
              { type: 'link', text: 'Configurations', href: '/' },
              { type: 'link', text: 'Journey View', href: '/journey' },
              { type: 'link', text: 'Pending Deletions', href: '/pending-deletions' },
              { type: 'divider' },
              // Bulk upload creates configurations, so it is hidden for view-only users.
              ...(canWrite
                ? [{ type: 'link' as const, text: 'Bulk Upload', href: '/configs/new?mode=bulk' }]
                : []),
              // Campaigns is for administrators and campaign uploaders; the route is
              // guarded by CampaignGuard too.
              ...(canUploadCampaigns
                ? [{ type: 'link' as const, text: 'Campaigns', href: '/campaigns' }]
                : []),
            ]}
          />
        }
        content={<><BreadcrumbNav /><Outlet /></>}
        toolsHide
      />
    </>
  );
}
